import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { AppShell } from "@/components/app-shell";
import { SystemThemeLayout, UserThemeLayout } from "@/components/theme-layouts";
import { SignInPage } from "@/pages/sign-in.page";
import { SignUpPage } from "@/pages/sign-up.page";
import { HomePage } from "@/pages/home.page";
import { ReportPage } from "@/pages/report.page";
import { DirectoryPeoplePage } from "@/pages/directory-people.page";
import { PersonDetailPage } from "@/pages/person-detail.page";
import { RunPage } from "@/pages/run.page";
import { NarrativePage } from "@/pages/narrative.page";
import { SourcesPage } from "@/pages/sources.page";
import { ListsPage } from "@/pages/lists.page";
import { ListsDetailPage } from "@/pages/lists-detail.page";
import {
  AnalyticsCockpitPage,
  AnalyticsDashboardDetailPage,
  AnalyticsDashboardsPage,
  AnalyticsGoalsPage,
  AnalyticsLayout,
  AnalyticsOverviewPage,
  AnalyticsReportDetailPage,
  AnalyticsReportsPage,
} from "@/pages/analytics.page";
import { OpportunitiesAddPage } from "@/pages/opportunities-add.page";
import { ProposalsPage } from "@/pages/proposals.page";
import {
  FormsDetailPage,
  FormsEditPage,
  FormsEnrichmentListPage,
  FormsLayout,
  FormsListPage,
  PublicFormPage,
} from "@/pages/forms.page";
import { DealsPage } from "@/pages/deals.page";
import { OpportunitiesDetailPage } from "@/pages/opportunities-detail.page";
import { SequencesPage } from "@/pages/sequences.page";
import { SequencesDetailPage } from "@/pages/sequences-detail.page";
import { DirectoryCompaniesPage } from "@/pages/directory-companies.page";
import { CompanyDetailPage } from "@/pages/company-detail.page";
import { AccountsPage } from "@/pages/user-accounts.page";
import { LeadsPage } from "@/pages/user-leads.page";
import { SignalsPage } from "@/pages/signals.page";
import { DataHealthPage } from "@/pages/data-health.page";
import { TasksPage } from "@/pages/tasks.page";
import { ActivityPage } from "@/pages/activity.page";
import {
  MeetingsAvailabilityPage,
  MeetingsLayout,
  MeetingsPage,
  MeetingsScheduledPage,
  MeetingsSchedulingPagesPage,
} from "@/pages/meetings.page";
import { BookPage } from "@/pages/book.page";
import { AiAssistantPage } from "@/pages/ai-assistant.page";
import {
  VisitorsAnalyticsPage,
  VisitorsCompaniesPage,
  VisitorsLayout,
  VisitorsPage,
  VisitorsPeoplePage,
  VisitorsSettingsPage,
  VisitorsWorkflowsPage,
} from "@/pages/visitors.page";
import {
  WorkflowDetailLayout,
  WorkflowEditPage,
  WorkflowEnrollmentPage,
  WorkflowOverviewPage,
  WorkflowSettingsPage,
  WorkflowsPage,
} from "@/pages/workflows.page";
import { SettingsLayout } from "@/pages/settings/layout";
import { SettingsProfilePage } from "@/pages/settings/profile.page";
import { SettingsIcpPage } from "@/pages/settings/icp.page";
import { SettingsPromptsPage } from "@/pages/settings/prompts.page";
import { SettingsCliPage } from "@/pages/settings/cli.page";
import { SettingsProvidersPage } from "@/pages/settings/providers.page";
import { SettingsPluginsPage } from "@/pages/settings/plugins.page";
import { SettingsAiPage } from "@/pages/settings/ai.page";
import { SettingsPromptDetailPage } from "@/pages/settings/prompts/detail.page";
import { SettingsProviderDetailPage } from "@/pages/settings/providers/detail.page";
import { SettingsPluginDetailPage } from "@/pages/settings/plugins/detail.page";
import { SettingsIntegrationsPage } from "@/pages/settings/integrations.page";
import { SettingsWorkspacePage } from "@/pages/settings/workspace.page";
import { SettingsContentCenterPage } from "@/pages/settings/content-center.page";
import { SettingsDealStagesPage } from "@/pages/settings/deal-stages.page";
import { SettingsGettingStartedPage } from "@/pages/settings/getting-started.page";
import { SettingsOrgConfigPage } from "@/pages/settings/org-config.page";
import { SettingsImportsExportsPage } from "@/pages/settings/imports-exports.page";
import { SettingsRecyclingBinPage } from "@/pages/settings/recycling-bin.page";
import {
  SettingsBillingPage,
  SettingsChromeExtensionPage,
  SettingsConversationsRecordingPage,
  SettingsIntentScoringPage,
  SettingsNotificationsPage,
  SettingsObjectsFieldsPage,
  SettingsPermissionProfilesPage,
  SettingsRemovalRequestsPage,
  SettingsSecurityPage,
  SettingsSequencesConfigPage,
  SettingsSystemActivityPage,
  SettingsTeamConversationsAdminPage,
  SettingsTeamMeetingsPage,
  SettingsTeamSharingPage,
  SettingsTeamsPage,
  SettingsWebsiteVisitorsSettingsPage,
} from "@/pages/settings/replicas";
import {
  SettingsPhoneNumbersPage,
  SettingsTeamDialerPage,
  SettingsDialerRecordingPage,
  SettingsDialerPurposesPage,
  SettingsDialerDispositionsPage,
  SettingsDialerVoicemailPage,
  SettingsDialerScorecardsPage,
} from "@/pages/settings/dialer.page";
import type { EngagementEventType } from "@sales-ops-amplify/shared";
import {
  EmailsHubPage,
  EmailsLayout,
  EmailsTemplatesPage,
} from "@/pages/emails.page";
import { SettingsMailboxesPage } from "@/pages/settings/mailboxes.page";
import { SettingsDeliverabilityPage } from "@/pages/settings/deliverability.page";
import { SettingsSnippetsPage } from "@/pages/settings/snippets.page";
import {
  CallsAllPage,
  CallsLayout,
  CallsNewPage,
  CallsUpcomingPage,
} from "@/pages/calls.page";
import { SoftphoneProvider } from "@/components/calls/softphone-context";
import { ConversationsDialerPage } from "@/pages/conversations-dialer.page";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: true, staleTime: 30_000 },
  },
});

function ShellLayout() {
  return (
    <SoftphoneProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </SoftphoneProvider>
  );
}

function ActivityByType({ type }: { type?: EngagementEventType }) {
  return <ActivityPage initialType={type ?? "all"} />;
}

function OpportunityLegacyRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/deals/${id}`} replace />;
}

/** `/dashboard` aliases the Analytics hub as the Sales Dashboard product surface. */
function DashboardAliasRedirect() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view");
  if (view === "deal-analytics") {
    return (
      <Navigate to="/analytics/dashboards/catalog:deal-analytics" replace />
    );
  }
  const rest = location.pathname.replace(/^\/dashboard/, "") || "";
  const target = `/analytics${rest}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<SystemThemeLayout />}>
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/book/:slug" element={<BookPage />} />
            <Route path="/f/:publicKey" element={<PublicFormPage />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<UserThemeLayout />}>
              <Route element={<ShellLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/dashboard" element={<DashboardAliasRedirect />} />
              <Route
                path="/dashboard/*"
                element={<DashboardAliasRedirect />}
              />
              <Route path="/ai-assistant" element={<AiAssistantPage />} />

              <Route path="/people" element={<DirectoryPeoplePage />} />
              <Route path="/people/:personId" element={<PersonDetailPage />} />
              <Route
                path="/people/:personId/:tab"
                element={<PersonDetailPage />}
              />
              <Route path="/lists" element={<ListsPage />} />
              <Route path="/lists/:listId" element={<ListsDetailPage />} />
              <Route path="/enrichment" element={<DataHealthPage />} />
              <Route path="/signals" element={<SignalsPage />} />

              <Route path="/sequences" element={<SequencesPage />} />
              <Route path="/sequences/:sequenceId" element={<SequencesDetailPage />} />
              <Route path="/emails" element={<EmailsLayout />}>
                <Route index element={<EmailsHubPage />} />
                <Route path="templates" element={<EmailsTemplatesPage />} />
              </Route>
              <Route path="/calls" element={<CallsLayout />}>
                <Route index element={<CallsAllPage />} />
                <Route path="upcoming" element={<CallsUpcomingPage />} />
                <Route path="new" element={<CallsNewPage />} />
              </Route>
              <Route path="/tasks" element={<TasksPage />} />

              <Route path="/meetings" element={<MeetingsLayout />}>
                <Route index element={<MeetingsPage />} />
                <Route path="scheduled" element={<MeetingsScheduledPage />} />
                <Route
                  path="scheduling-pages"
                  element={<MeetingsSchedulingPagesPage />}
                />
                <Route
                  path="availability"
                  element={<MeetingsAvailabilityPage />}
                />
              </Route>
              <Route path="/conversations" element={<ActivityByType />} />
              <Route
                path="/conversations/dialer"
                element={<ConversationsDialerPage />}
              />
              <Route path="/deals" element={<DealsPage />} />
              <Route path="/deals/add" element={<OpportunitiesAddPage />} />
              <Route path="/deals/:id" element={<OpportunitiesDetailPage />} />

              <Route path="/workflows" element={<WorkflowsPage />} />
              <Route path="/workflows/:workflowId" element={<WorkflowDetailLayout />}>
                <Route index element={<WorkflowOverviewPage />} />
                <Route path="edit" element={<WorkflowEditPage />} />
                <Route path="enrollment" element={<WorkflowEnrollmentPage />} />
                <Route path="settings" element={<WorkflowSettingsPage />} />
              </Route>
              <Route path="/analytics" element={<AnalyticsLayout />}>
                <Route index element={<AnalyticsOverviewPage />} />
                <Route path="dashboards" element={<AnalyticsDashboardsPage />} />
                <Route
                  path="dashboards/:dashboardId"
                  element={<AnalyticsDashboardDetailPage />}
                />
                <Route path="reports" element={<AnalyticsReportsPage />} />
                <Route
                  path="reports/:reportId"
                  element={<AnalyticsReportDetailPage />}
                />
                <Route path="goals" element={<AnalyticsGoalsPage />} />
                <Route path="cockpit" element={<AnalyticsCockpitPage />} />
              </Route>
              <Route path="/websites" element={<Navigate to="/visitors" replace />} />
              <Route path="/visitors" element={<VisitorsLayout />}>
                <Route index element={<VisitorsPage />} />
                <Route path="companies" element={<VisitorsCompaniesPage />} />
                <Route path="people" element={<VisitorsPeoplePage />} />
                <Route path="analytics" element={<VisitorsAnalyticsPage />} />
                <Route path="workflows" element={<VisitorsWorkflowsPage />} />
                <Route path="settings" element={<VisitorsSettingsPage />} />
              </Route>
              <Route path="/forms" element={<FormsLayout />}>
                <Route index element={<FormsListPage />} />
                <Route path="enrichment" element={<FormsEnrichmentListPage />} />
                <Route path=":formId/edit" element={<FormsEditPage />} />
                <Route path=":formId" element={<FormsDetailPage />} />
              </Route>
              <Route path="/proposals" element={<ProposalsPage />} />
              <Route path="/companies" element={<DirectoryCompaniesPage />} />
              <Route path="/companies/:companyId" element={<CompanyDetailPage />} />
              <Route
                path="/companies/:companyId/:tab"
                element={<CompanyDetailPage />}
              />
              <Route path="/user/leads" element={<LeadsPage />} />
              <Route
                path="/user/leads/:leadId"
                element={<PersonDetailPage />}
              />
              <Route
                path="/user/leads/:leadId/:tab"
                element={<PersonDetailPage />}
              />
              <Route path="/user/accounts" element={<AccountsPage />} />
              <Route
                path="/user/accounts/:accountId"
                element={<CompanyDetailPage />}
              />
              <Route
                path="/user/accounts/:accountId/:tab"
                element={<CompanyDetailPage />}
              />

              <Route path="/narrative" element={<NarrativePage />} />
              <Route path="/reports/:id" element={<ReportPage />} />
              <Route path="/runs/:id" element={<RunPage />} />

              
              <Route path="/intake" element={<SourcesPage />} />
              <Route path="/pipeline" element={<Navigate to="/deals" replace />} />
              <Route path="/pipeline/*" element={<Navigate to="/deals" replace />} />
              <Route path="/opportunities" element={<Navigate to="/deals" replace />} />
              <Route path="/opportunities/add" element={<Navigate to="/deals/add" replace />} />
              <Route path="/opportunities/:id" element={<OpportunityLegacyRedirect />} />
              <Route path="/activity" element={<Navigate to="/conversations" replace />} />
              <Route path="/config" element={<Navigate to="/settings/account" replace />} />

              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/settings/account" replace />} />

                {/* Pins */}
                <Route path="getting-started" element={<SettingsGettingStartedPage />} />

                {/* Personal */}
                <Route path="account" element={<SettingsProfilePage />} />
                <Route path="account/mailboxes" element={<SettingsMailboxesPage />} />
                <Route path="account/voices" element={<SettingsPhoneNumbersPage />} />
                <Route path="account/mfa" element={<Navigate to="/settings/account" replace />} />
                <Route path="account/custom-fields" element={<Navigate to="/settings/account" replace />} />
                <Route path="account/email-settings" element={<Navigate to="/settings/account" replace />} />
                <Route path="account/conversations" element={<Navigate to="/settings/account" replace />} />
                <Route path="notifications" element={<SettingsNotificationsPage />} />
                <Route path="chrome-extension" element={<SettingsChromeExtensionPage />} />
                <Route path="conversations" element={<SettingsConversationsRecordingPage />} />

                {/* Workspace */}
                <Route path="workspace-overview" element={<SettingsWorkspacePage />} />
                <Route path="users" element={<SettingsWorkspacePage />} />
                <Route path="teams" element={<SettingsTeamsPage />} />
                <Route path="permission-profiles" element={<SettingsPermissionProfilesPage />} />
                <Route path="security" element={<SettingsSecurityPage />} />
                <Route path="security/support-access" element={<SettingsSecurityPage />} />

                <Route path="deliverability-suite/overview" element={<SettingsDeliverabilityPage />} />
                <Route path="deliverability-suite/domains" element={<SettingsDeliverabilityPage />} />
                <Route path="deliverability-suite/mailboxes" element={<SettingsDeliverabilityPage />} />
                <Route path="deliverability-suite/bounce-logs" element={<SettingsDeliverabilityPage />} />

                <Route path="plans" element={<SettingsBillingPage />} />
                <Route path="plans/license-settings" element={<SettingsBillingPage />} />
                <Route path="features-add-ons" element={<SettingsBillingPage />} />
                <Route path="credits" element={<SettingsAiPage />} />
                <Route path="ai-runs" element={<SettingsBillingPage />} />

                <Route path="integrations" element={<SettingsIntegrationsPage />} />
                <Route path="integrations/mcp" element={<SettingsPluginsPage />} />

                <Route path="personas" element={<SettingsIcpPage />} />
                <Route path="intent-data" element={<SettingsIntentScoringPage />} />
                <Route path="websites" element={<SettingsWebsiteVisitorsSettingsPage />} />
                <Route path="signals" element={<SettingsProvidersPage />} />
                <Route path="signals/:id" element={<SettingsProviderDetailPage />} />
                <Route path="scoring-models" element={<SettingsIntentScoringPage />} />

                <Route path="content-center" element={<SettingsContentCenterPage />} />
                <Route path="org" element={<SettingsOrgConfigPage />} />

                <Route path="snippets" element={<SettingsSnippetsPage />} />
                <Route path="tracking-subdomains" element={<SettingsSequencesConfigPage />} />
                <Route path="sequence" element={<SettingsPromptsPage />} />
                <Route path="sequence/:kind" element={<SettingsPromptDetailPage />} />

                <Route path="calls" element={<SettingsTeamDialerPage />} />
                <Route
                  path="calls/recording"
                  element={<SettingsDialerRecordingPage />}
                />
                <Route
                  path="calls/purposes"
                  element={<SettingsDialerPurposesPage />}
                />
                <Route
                  path="calls/dispositions"
                  element={<SettingsDialerDispositionsPage />}
                />
                <Route
                  path="calls/voicemail-drops"
                  element={<SettingsDialerVoicemailPage />}
                />
                <Route
                  path="calls/scorecards"
                  element={<SettingsDialerScorecardsPage />}
                />
                <Route path="team-conversations" element={<SettingsTeamConversationsAdminPage />} />
                <Route path="team-conversations/users" element={<SettingsTeamConversationsAdminPage />} />
                <Route path="team-conversations/trackers" element={<SettingsTeamConversationsAdminPage />} />
                <Route
                  path="team-conversations/scorecards"
                  element={<SettingsDialerScorecardsPage />}
                />
                <Route path="team-conversations/custom-field-prompts" element={<SettingsTeamConversationsAdminPage />} />
                <Route path="team-meetings" element={<SettingsTeamMeetingsPage />} />
                <Route path="defaults/people" element={<SettingsTeamSharingPage />} />

                <Route path="data-requests" element={<SettingsSystemActivityPage />} />
                <Route path="activity-log" element={<SettingsSystemActivityPage />} />

                {/* Data management */}
                <Route path="leads" element={<SettingsObjectsFieldsPage />} />
                <Route path="accounts" element={<SettingsObjectsFieldsPage />} />
                <Route path="opportunities" element={<SettingsDealStagesPage />} />

                <Route path="imports-and-exports/leads" element={<SettingsImportsExportsPage />} />
                <Route path="imports-and-exports/contacts" element={<Navigate to="/settings/imports-and-exports/leads" replace />} />
                <Route path="imports-and-exports/accounts" element={<SettingsImportsExportsPage />} />
                <Route path="imports-and-exports/exports" element={<SettingsImportsExportsPage />} />
                <Route path="imports-and-exports/enriched-csv" element={<SettingsImportsExportsPage />} />
                <Route path="imports-and-exports/csv-export-settings" element={<SettingsImportsExportsPage />} />
                <Route path="recycling-bin" element={<Navigate to="/settings/recycling-bin/accounts" replace />} />
                <Route path="recycling-bin/accounts" element={<SettingsRecyclingBinPage />} />
                <Route path="recycling-bin/leads" element={<SettingsRecyclingBinPage />} />
                <Route path="removal-request" element={<SettingsRemovalRequestsPage />} />

                {/* Deep links still used by content-center / redirects */}
                <Route path="ai" element={<SettingsAiPage />} />
                <Route path="cli" element={<SettingsCliPage />} />
                <Route path="prompts" element={<Navigate to="/settings/sequence" replace />} />
                <Route path="prompts/:kind" element={<SettingsPromptKindRedirect />} />
                <Route path="providers" element={<Navigate to="/settings/signals" replace />} />
                <Route path="providers/:id" element={<SettingsProviderLegacyRedirect />} />
                <Route path="plugins" element={<Navigate to="/settings/integrations/mcp" replace />} />
                <Route path="plugins/:id" element={<SettingsPluginDetailPage />} />

                {/* Legacy redirects */}
                <Route path="profile" element={<Navigate to="/settings/account" replace />} />
                <Route path="workspace" element={<Navigate to="/settings/workspace-overview" replace />} />
                <Route path="icp" element={<Navigate to="/settings/personas" replace />} />
              </Route>
              </Route>
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function SettingsPromptKindRedirect() {
  const { kind } = useParams<{ kind: string }>();
  return <Navigate to={`/settings/sequence/${kind}`} replace />;
}

function SettingsProviderLegacyRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/settings/signals/${id}`} replace />;
}
